// src/pages/admin/AdminSponsors.js
// Manage multi-tier sponsors: Founding Partner, Gold, Community Supporter

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  collection, addDoc, getDocs, updateDoc, deleteDoc,
  doc, query, where, serverTimestamp
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const TIERS = [
  { value: 'founding', label: 'Town Champion 2026',  price: '€1,500/yr', color: '#4A544A', textColor: 'white', slots: '1 slot — annual, exclusive' },
  { value: 'gold',     label: 'Community Partner',   price: '€50/month',  color: '#C4873A', textColor: 'white', slots: 'Up to 3 per town' },
  { value: 'supporter',label: 'Community Supporter',  price: 'From €3/month', color: '#EBF2EB', textColor: '#4A544A', slots: 'Unlimited' },
];

const ALL_PAGES = ['events', 'community', 'services', 'skills'];

const EMPTY = {
  name: '', tier: 'gold', emoji: '🏢',
  tagline: '', website: '', contactEmail: '',
  contactPhone: '', townId: 'loughrea',
  active: true, tierOrder: 2, pages: [...ALL_PAGES],
};

const TIER_ORDER = { founding: 1, gold: 2, supporter: 3 };

const AdminSponsors = () => {
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState([]);
  const [towns, setTowns] = useState([]);
  const [selectedTown, setSelectedTown] = useState('loughrea');
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [logoFile, setLogoFile] = useState(null);

  useEffect(() => { loadAll(); }, [selectedTown]);

  const loadAll = async () => {
    const townsSnap = await getDocs(collection(db, 'towns'));
    setTowns(townsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    const q = query(collection(db, 'sponsors'), where('townId', '==', selectedTown));
    const snap = await getDocs(q);
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    data.sort((a, b) => (TIER_ORDER[a.tier] || 9) - (TIER_ORDER[b.tier] || 9));
    setSponsors(data);
  };

  const startEdit = (sponsor) => {
    setForm({
      name: sponsor.name || '',
      tier: sponsor.tier || 'gold',
      emoji: sponsor.emoji || '🏢',
      logoUrl: sponsor.logoUrl || null,
      tagline: sponsor.tagline || '',
      website: sponsor.website || '',
      contactEmail: sponsor.contactEmail || '',
      contactPhone: sponsor.contactPhone || '',
      active: sponsor.active !== false,
      tierOrder: sponsor.tierOrder || 9,
      pages: sponsor.pages || [...ALL_PAGES],
    });
    setEditId(sponsor.id);
    setAdding(true);
    window.scrollTo(0,0);
    window.scrollTo(0,0);
  };

  const set = (k, v) => setForm(f => ({
    ...f, [k]: v,
    ...(k === 'tier' ? { tierOrder: TIER_ORDER[v] || 9 } : {}),
  }));

  const handleSave = async () => {
    if (!form.name) { toast.error('Sponsor name is required'); return; }

    // Check founding partner limit
    if (form.tier === 'founding' && !editId) {
      const existing = sponsors.filter(s => s.tier === 'founding' && s.active);
      if (existing.length >= 1) {
        toast.error('Only one Town Champion per town — remove the existing one first');
        return;
      }
    }

    setSaving(true);
    try {
      let logoUrl = form.logoUrl || null;
      if (logoFile) {
        const imgRef = ref(storage, `sponsors/${Date.now()}_${logoFile.name}`);
        await uploadBytes(imgRef, logoFile);
        logoUrl = await getDownloadURL(imgRef);
        setLogoFile(null);
      }
      const saveData = { ...form, logoUrl };

      if (editId) {
        await updateDoc(doc(db, 'sponsors', editId), { ...saveData, updatedAt: serverTimestamp() });
        toast.success('Sponsor updated');
        setEditId(null);
      } else {
        await addDoc(collection(db, 'sponsors'), { ...saveData, townId: selectedTown, createdAt: serverTimestamp() });
        toast.success(`${form.name} added`);
      }
      setForm(EMPTY);
      setAdding(false);
      loadAll();
    } catch (err) { console.error(err); toast.error('Failed to save sponsor'); }
    setSaving(false);
  };

  const toggleActive = async (id, current) => {
    await updateDoc(doc(db, 'sponsors', id), { active: !current });
    loadAll();
    toast.success(current ? 'Sponsor hidden' : 'Sponsor showing');
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name} as a sponsor?`)) return;
    await deleteDoc(doc(db, 'sponsors', id));
    loadAll();
    toast.success('Removed');
  };

  const sponsorsByTier = tier => sponsors.filter(s => s.tier === tier);

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>

      <button onClick={() => navigate('/admin')} style={bk}>
        <div style={bkCircle}>←</div>
        <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>Admin</span>
      </button>

      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>💰 Sponsors</div>
      <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500, marginBottom: 16 }}>
        Manage sponsor tiers and revenue for each town
      </div>

      {/* Tier overview */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {TIERS.map(tier => (
          <div key={tier.value} style={{ background: 'var(--white)', borderRadius: 14, padding: '12px 16px', boxShadow: 'var(--shadow-sm)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: tier.color, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{tier.label}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500 }}>{tier.slots}</div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--sage-deep)' }}>
              {sponsorsByTier(tier.value).filter(s => s.active).length} active
            </div>
          </div>
        ))}
      </div>

      {/* Town selector */}
      <select className="form-input" style={{ marginBottom: 14 }} value={selectedTown} onChange={e => setSelectedTown(e.target.value)}>
        {towns.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>

      {/* Add sponsor button */}
      <button onClick={() => { setAdding(!adding); setEditId(null); }} style={{ width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 14, padding: 15, fontSize: 15, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', marginBottom: 16 }}>
        {adding ? '✕ Cancel' : '+ Add sponsor'}
      </button>

      {/* Add form */}
      {adding && (
        <div style={{ background: 'var(--white)', borderRadius: 18, padding: 18, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Tier selection */}
            <div>
              <div style={fl}>Tier</div>
              <div style={{ display: 'flex', gap: 8 }}>
          {TIERS.map(tier => (
                  <button
                    key={tier.value}
                    onClick={() => set('tier', tier.value)}
                    style={{
                      flex: 1, padding: '10px 6px', borderRadius: 12, border: 'none',
                      background: form.tier === tier.value ? 'var(--sage-dark)' : 'var(--sage-pale)',
                      color: form.tier === tier.value ? 'white' : 'var(--sage-dark)',
                      fontSize: 11, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer',
                    }}
                  >
                    {tier.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'1.2px',color:'var(--muted)',marginBottom:6}}>Sponsor logo (optional)</div>
              <label style={{display:'flex',alignItems:'center',justifyContent:'center',background:'var(--sage-pale)',border:'1.5px dashed #AAC1AC',borderRadius:12,padding:14,cursor:'pointer',fontSize:13,fontWeight:600,color:'var(--sage-dark)'}}>
                <input type='file' accept='image/*' style={{display:'none'}} onChange={e=>setLogoFile(e.target.files[0])} />
                {logoFile ? logoFile.name : form.logoUrl ? 'Logo uploaded - tap to replace' : '📷 Upload logo'}
              </label>
            </div>

            {[
              ['Sponsor name *', 'name', 'e.g. Loughrea Credit Union'],
                          ['Tagline (optional)', 'tagline', 'e.g. Serving our community since 1963'],
              ['Website (optional)', 'website', 'https://...'],
              ['Contact email', 'contactEmail', 'For your billing records'],
              ['Contact phone', 'contactPhone', 'For your billing records'],
            ].map(([lbl, key, ph]) => (
              <div key={key}>
                <div style={fl}>{lbl}</div>
                <input className="form-input" value={form[key]} onChange={e => set(key, e.target.value)} placeholder={ph} />
              </div>
            ))}

            <div style={{ marginBottom: 12 }}>
              <div style={fl}>Show on which pages?</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[
                  ['events', 'Events'],
                  ['community', 'Community'],
                  ['services', 'Services'],
                  ['skills', 'Skill Share'],
                ].map(([key, label]) => {
                  const checked = (form.pages || []).includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => set('pages', checked ? (form.pages || []).filter(p => p !== key) : [...(form.pages || []), key])}
                      style={{
                        padding: '8px 14px', borderRadius: 100, border: 'none', cursor: 'pointer',
                        fontSize: 13, fontWeight: 700, fontFamily: 'Nunito',
                        background: checked ? 'var(--sage-deep)' : '#F0F0EE',
                        color: checked ? 'white' : 'var(--muted)',
                      }}
                    >
                      {checked ? '✓ ' : ''}{label}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
                Most sponsors want every page — untick any this sponsor shouldn't appear on.
              </div>
            </div>

            <button onClick={handleSave} disabled={saving} style={{ background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 14, padding: 15, fontSize: 15, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : editId ? 'Save changes' : 'Add sponsor'}
            </button>
          </div>
        </div>
      )}

      {/* Sponsors by tier */}
      {TIERS.map(tier => {
        const tierSponsors = sponsorsByTier(tier.value);
        if (tierSponsors.length === 0) return null;
        return (
          <div key={tier.value}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', margin: '16px 0 8px' }}>
              {tier.label} ({tierSponsors.length})
            </div>
            {tierSponsors.map(sponsor => (
              <div key={sponsor.id} style={{ background: 'var(--white)', borderRadius: 14, padding: '14px 16px', marginBottom: 8, boxShadow: 'var(--shadow-sm)', opacity: !sponsor.active ? 0.5 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {sponsor.logoUrl ? <img src={sponsor.logoUrl} alt={sponsor.name} style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 6 }} /> : <span>{sponsor.emoji}</span>}
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{sponsor.name}</span>
                  </div>
                  <span style={{ fontSize: 10, background: sponsor.active ? 'var(--sage-pale)' : '#F0F0EE', color: sponsor.active ? 'var(--sage-deep)' : 'var(--muted)', padding: '2px 8px', borderRadius: 100, fontWeight: 700 }}>
                    {sponsor.active ? 'Active' : 'Hidden'}
                  </span>
                </div>
                {sponsor.tagline && <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginBottom: 8 }}>{sponsor.tagline}</div>}
                <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600, marginBottom: 8 }}>
                  Shows on: {(sponsor.pages || ALL_PAGES).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => startEdit(sponsor)} style={{ flex: 1, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                    Edit
                  </button>
                  <button onClick={() => toggleActive(sponsor.id, sponsor.active)} style={{ flex: 1, background: '#F0F0EE', color: 'var(--ink)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                    {sponsor.active ? 'Hide' : 'Show'}
                  </button>
                  <button onClick={() => handleDelete(sponsor.id, sponsor.name)} style={{ background: 'var(--error-pale)', color: 'var(--error)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        );
      })}
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

const fl = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 6 };
const bk = { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20 };
const bkCircle = { width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: 'var(--sage-dark)', boxShadow: 'var(--shadow-sm)' };

export default AdminSponsors;
